'use client';

import {z} from 'zod';
import {css} from '@panda/css';
import {Box, Grid, GridItem} from '@panda/jsx';
import {Asset} from '@/components/atoms/asset';
import {zodResolver} from '@hookform/resolvers/zod';
import {useRedeemStepsContext} from '@/context/RedeemStepsContext';
import {Container, ContentBlock, SafeHTML} from '@/components';
import {useRouter, useSearchParams} from 'next/navigation';
import {FormProvider, useForm} from 'react-hook-form';
import {mapClientError} from '@/lib/utils/error.utils';
import {ReactNode, SyntheticEvent, useEffect} from 'react';
import {onEmitClickEvent} from '@/lib/utils/analytics';
import {useLocale, useMessages} from '@epic-mw/i18n';
import {SectionSideBySideFieldsFragment} from '@common/hygraph-schema';
import {ConfirmEulaButton} from '@/components/molecules/button';
import {AnalyticEventProps, ConnectedPlatform} from '@/types';
import {getMessage} from '@/lib/utils/i18n.utils';
import {
	getGiftCardRedirectUrl,
	getRedeemSuccessRedirectUrl,
	hasExceededLimit
} from '@/lib/utils/giftCard.utils';
import {
	useConnectedPlatforms,
	useDebounce,
	useGiftCardAmounts,
	useGiftCardEulaPrompt,
	useGiftCardRedeem,
	useGiftCardStore,
	useMediaQuery,
	useQuickLinks,
	useAccountDisplayName,
	useAccessCheck
} from '@/hooks';
import {
	Button,
	ButtonProps,
	Divider,
	EpicGamesIcon,
	InfoCircleIcon,
	Link,
	Stack,
	Text,
	ChipGroup,
	Chip,
	PlayStationIcon,
	NintendoSwitchIcon,
	XboxIcon,
	ProgressIndicator
} from '@eds-web/react';

const emitRedeemCodeEvent = <T extends HTMLElement>(
	event: SyntheticEvent<T> | null | undefined,
	eventValue?: Record<string, string>
) => {
	onEmitClickEvent({
		eventCategory: 'button',
		interactionType: 'click',
		eventAction: 'redeemCode',
		eventLabel: 'Redeem Code',
		eventValue: {
			component: 'AddFundsStep',
			section: 'redeem',
			subView: 'Confirm',
			interactedElement: 'Buttom_Add Funds',
			...eventValue
		}
	})(event);
};

const emitChangeAccountEvent = onEmitClickEvent({
	eventCategory: 'button',
	interactionType: 'click',
	eventAction: 'changeAccount',
	eventLabel: 'Change Epic Account',
	eventValue: {
		component: 'AddFundsStep',
		section: 'redeem',
		subView: 'Confirm',
		interactedElement: 'Change'
	}
});

const acceptDeclineTermsAnalyticProps: AnalyticEventProps = {
	eventCategory: 'button',
	interactionType: 'click',
	eventAction: 'acceptDeclineTerms',
	eventLabel: 'Accept or Decline Terms',
	eventValue: {
		component: 'AddFundsStep',
		section: 'redeem',
		subView: 'Accept Terms',
		interactedElement: 'Button_Accept/Decline'
	}
};

const buttonStyles = css({
	minWidth: '200px',
	width: {
		mdDown: '100%'
	}
});

const linkStyles = css({
	fontSize: {md: '12px !important'},
	color: 'eds.text.secondary !important',
	textDecoration: 'underline !important'
});

const boldStyles = css({
	fontWeight: 'bold !important'
});

const schema = z.object({
	agreementAccepted: z.boolean()
});

type RedeemFormValues = z.infer<typeof schema>;

interface Props extends SectionSideBySideFieldsFragment {
	className?: string;
}

export const SideBySideRedeemSection = (props: Props) => {
	const {code} = useRedeemStepsContext();
	const locale = useLocale();
	const router = useRouter();
	const searchParams = useSearchParams();

	/**
	 * Render AddFunds Page
	 */
	const methods = useForm<RedeemFormValues>({
		resolver: zodResolver(schema as any),
		defaultValues: {
			agreementAccepted: false
		}
	});

	const messages = useMessages();
	const isMdDown = useMediaQuery('mdDown');
	const displayName = useAccountDisplayName();
	const agreementAccepted = methods.watch('agreementAccepted');
	const {verify} = useGiftCardStore();
	const agreement = useGiftCardEulaPrompt();
	const amounts = useGiftCardAmounts();
	const connectedPlatforms = useConnectedPlatforms();
	const quickLinks = useQuickLinks(['accountBalanceTerms', 'giftCardTerms', 'faqAccountBalance']);
	const accessCheck = useAccessCheck();
	const {isSubmitting, isSubmitSuccessful} = methods.formState;

	const redeemState = useGiftCardRedeem({
		onSuccess: (result) => {
			if (!result) return; // false positives
			emitRedeemCodeEvent(null, {status: 'success', codeRedeemed: 'true'});
			if (hasExceededLimit(result)) {
				emitRedeemCodeEvent(null, {
					status: 'error',
					error: 'epic.app.error.addFunds.maxAmount'
				});
			}
		},
		onError: (error) => {
			emitRedeemCodeEvent(null, {status: 'error', error});
		}
	});

	const {trigger, isMutating: isLoadingRedeem = isSubmitting, redeemed} = redeemState;
	const completedStep = isSubmitSuccessful && redeemed;

	const giftCardError =
		redeemState.error ||
		verify?.error ||
		(verify?.data && hasExceededLimit(verify.data)
			? {error: 'epic.app.error.addFunds.maxAmount'}
			: undefined);

	const errorMessage =
		typeof giftCardError?.error === 'string' &&
		mapClientError(messages, giftCardError.error, {
			faqAccountBalance: quickLinks.faqAccountBalance,
			maxAmount: amounts.maxBalance,
			locale
		});

	const getIconFromPlatform = (platform: ConnectedPlatform['platform']) => {
		switch (platform) {
			case 'PSN':
				return <PlayStationIcon />;
			case 'SWITCH':
				return <NintendoSwitchIcon />;
			case 'XBL':
				return <XboxIcon />;
			default:
				return null;
		}
	};

	const handleChangeAccount = useDebounce((e?: SyntheticEvent<HTMLAnchorElement>) => {
		emitChangeAccountEvent(e);
		router.push(`/api/id/change?code=${code}`);
	});

	const isAgreementAccepted = async () => {
		const key = 'agreementAccepted';
		methods.setValue(key, true, {shouldValidate: true});
		const isValid = await methods.trigger(key);
		return isValid && methods.getValues(key);
	};

	const handleRedeemNow = methods.handleSubmit(async () => {
		if (await isAgreementAccepted()) {
			accessCheck.check({
				redirectIfFailUrl: getGiftCardRedirectUrl(code),
				//eslint-disable-next-line @typescript-eslint/no-misused-promises
				action: (result) => {
					return trigger({newCode: code, ...result});
				}
			});
		}
	});

	const loaderOrValue = (loading: boolean, node: ReactNode) => {
		if (loading) {
			return (
				<Box color="token(colors.eds.palette.grayscale.gray120)">
					<ProgressIndicator variant="circular" aria-hidden />
				</Box>
			);
		}
		return node;
	};

	const buttonProps: ButtonProps = {
		size: 'xl',
		type: 'button',
		className: buttonStyles,
		disabled: isLoadingRedeem || !!giftCardError || completedStep,
		loading: isLoadingRedeem || accessCheck.isLoading,
		loadingLabel: messages['epic.app.addFunds.cta'],
		children: messages['epic.app.addFunds.cta']
	};

	useEffect(() => {
		if (isSubmitSuccessful && redeemed) {
			const source = searchParams.get('source') || '';
			const toUrl = searchParams.get('redirectUrl') || '';
			const redirectUrl = ['account-management', 'amt', 'egs', 'egs-launcher'].includes(
				source
			)
				? getRedeemSuccessRedirectUrl(toUrl)
				: undefined;

			router.push(redirectUrl ? redirectUrl : `/spend`);
		}
	}, [isSubmitSuccessful, redeemed]);

	return (
		<Container
			display="grid"
			placeItems="center"
			bg={isMdDown ? 'transparent' : undefined}
			boxClassName={css({overflow: 'hidden'})}
			width={{base: 'calc(100% - 48px)', lg: '80%'}}
			pb={{base: '40px', smDown: '24px'}}
			pt={{base: '80px', smDown: '24px'}}>
			<Grid
				w="100%"
				columns={12}
				gap={4}
				alignItems={{lgDown: 'center'}}
				gridTemplateColumns="3fr auto">
				<GridItem className={css({zIndex: 10, wordWrap: 'anywhere'})}>
					{props.content && (
						<ContentBlock {...props.content}>
							<Stack gap={24}>
								<Box
									p="16px"
									borderRadius="16px"
									backgroundColor="eds.background.elevated.low.transparent">
									<Stack direction="row" justify="space-between" align="center">
										<Stack gap={12} direction="row" align="center">
											<EpicGamesIcon
												color="onAccent"
												style={{
													background: 'white',
													padding: '5px',
													borderRadius: '6.6px',
													width: '44px',
													height: 'auto'
												}}
											/>
											<Text variant="uiMDBold">{displayName}</Text>
										</Stack>
										<Link
											href="#"
											className={linkStyles}
											onClick={handleChangeAccount}>
											{messages['epic.app.addFunds.change']}
										</Link>
									</Stack>

									{!connectedPlatforms.length ? null : (
										<Box mt="16px">
											<Stack gap={16}>
												<Divider />
												<ChipGroup emphasis="low" gap={8} align="center">
													{connectedPlatforms.map(
														({platform, externalDisplayName}) => {
															const label =
																messages[
																	`epic.app.platform.${platform}`
																] || platform;
															return (
																<Chip
																	className={css({
																		p: '8px 16px !important',
																		'& span': {
																			direction: 'ltr'
																		}, // Fix for all, specifically nintendo - FDT-4636.
																		'& button': {
																			display:
																				'none !important'
																		},
																		'& svg': {
																			w: '20px !important',
																			h: '20px !important'
																		}
																	})}
																	key={platform}
																	label={
																		externalDisplayName || label
																	}
																	removeLabel={label}
																	onRemove={undefined}
																	icon={getIconFromPlatform(
																		platform
																	)}
																/>
															);
														}
													)}
												</ChipGroup>
											</Stack>
										</Box>
									)}
								</Box>

								<Stack gap={8}>
									<Text
										variant="eyebrowSM"
										color="secondary"
										className={boldStyles}>
										{messages['epic.app.addFunds.accountBalance']}
									</Text>

									<Box display="flex" justifyContent="space-between">
										<Text variant="uiMD">
											{messages['epic.app.addFunds.currentBalance']}
										</Text>
										{loaderOrValue(
											amounts.isLoading,
											<Text variant="uiMD">{amounts.currentBalance}</Text>
										)}
									</Box>

									<Box display="flex" justifyContent="space-between">
										<Text variant="uiMD">
											{messages['epic.app.addFunds.cardAmount']}
										</Text>
										{loaderOrValue(
											amounts.isLoading,
											<Text variant="uiMD">{amounts.cardAmount}</Text>
										)}
									</Box>

									<Divider />

									<Box display="flex" justifyContent="space-between">
										<Text variant="uiMD" className={boldStyles}>
											{messages['epic.app.addFunds.newBalance']}
										</Text>
										{loaderOrValue(
											amounts.isLoading,
											<Text
												variant="uiMD"
												className={boldStyles}
												color={giftCardError ? 'critical' : 'success'}>
												{amounts.newBalance}
											</Text>
										)}
									</Box>
								</Stack>

								<Text variant="paragraphSM" style={{fontStyle: 'italic'}}>
									{getMessage(messages, 'epic.app.addFunds.terms', [
										<Link
											href={quickLinks.accountBalanceTerms}
											className={boldStyles}
											external
											externalLabel="open a new tab">
											{messages['epic.app.terms.accountBalance']}
										</Link>,
										<Link
											href={quickLinks.giftCardTerms}
											className={boldStyles}
											external
											externalLabel="open a new tab">
											{messages['epic.app.terms.giftCard']}
										</Link>
									])}
								</Text>

								<FormProvider {...methods}>
									<form>
										{!agreementAccepted && agreement.show ? (
											<ConfirmEulaButton
												{...buttonProps}
												analytics={acceptDeclineTermsAnalyticProps}
												eulaId={agreement.key}
												eulaVersion={agreement.version}
												onClick={(e) =>
													emitRedeemCodeEvent(e, {
														status: 'initiate',
														showAgreement: 'true',
														locale,
														eulaId: agreement.key,
														eulaVersion: String(agreement.version)
													})
												}
												onAcceptSuccessful={() => {
													void handleRedeemNow();
												}}
												onDecline={() => {
													methods.setValue('agreementAccepted', false, {
														shouldValidate: true
													});
													void methods.trigger('agreementAccepted');
												}}
											/>
										) : (
											<Button
												{...buttonProps}
												onClick={(e) => {
													emitRedeemCodeEvent(e, {
														status: 'initiate'
													});
													void handleRedeemNow(e);
												}}
											/>
										)}
									</form>
								</FormProvider>

								{typeof errorMessage === 'string' && (
									<Stack gap={4} direction="row" align="center">
										<InfoCircleIcon color="critical" />
										<Text
											variant="uiMDBold"
											color="critical"
											className="nested-link">
											<SafeHTML
												dangerouslySetInnerHTML={{__html: errorMessage}}
											/>
										</Text>
									</Stack>
								)}
							</Stack>
						</ContentBlock>
					)}
				</GridItem>

				{!isMdDown && (
					<GridItem margin={{base: '-3em', lgDown: '0 -8em'}}>
						<Box
							position="relative"
							className={css({
								width: '688px',
								'& img': {
									position: 'relative !important',
									aspectRatio: '688 / 604'
								},
								lgDown: {
									width: '600px'
								}
							})}>
							{props.image?.image && (
								<Asset
									fill
									priority
									unoptimized
									asset={props.image?.image}
									className={css({
										objectFit: 'contain',
										smDown: {
											width: 'full'
										}
									})}
								/>
							)}
						</Box>
					</GridItem>
				)}
			</Grid>
		</Container>
	);
};
